<script lang="ts">
  import { onMount, createEventDispatcher } from "svelte";
  import Scroller from "scroller";
  import { MAP_WIDTH, MAP_HEIGHT } from "../../constants";
  import { distance, center, midpoint } from "../../support/geometry";
  import type { Writable } from "svelte/store";

  // min/max zoom level
  export let maxZoom = 3;
  export let minZoom = 0.25;

  // Dimensions of content
  export let width = MAP_WIDTH;
  export let height = MAP_HEIGHT;

  // Initial focus rectangle, will center on screen at boot
  export let focus = null;

  // Location tracking
  export let isTracking = false;
  export let camera = null;

  export let positionX: Writable<number>;
  export let positionY: Writable<number>;
  export let positionZ: Writable<number>;
  export let initialZoom = 1;
  export let autofit = false;

  const dispatch = createEventDispatcher();

  $: {
    const pos = $camera;

    if (pos && isTracking) {
      scroller.scrollToViewportCenter(pos.x, pos.y, true);
    }
  }

  let ready = false;
  let mousedown = false;

  export let container = null;

  let content: HTMLElement, scroller;
  let clientWidth = 1;
  let clientHeight = 1;

  $: x = $positionX;
  $: y = $positionY;
  $: z = $positionZ;

  function render(dx: number, dy: number, dz: number) {
    positionX.set(dx);
    positionY.set(dy);
    positionZ.set(dz);

    dispatch("zoom", scroller.getValues().zoom);
  }

  function fitToContainer() {
    // Scale the map to fit the screen if it can, otherwise scale to fit center
    var scaledZoom = Math.max(clientHeight / height, clientWidth / width);

    scroller.scrollToViewportCenter(width * 0.5, height * 0.5, false, scaledZoom);
  }

  onMount(function () {
    scroller = new Scroller(render, {
      maxZoom: maxZoom,
      minZoom: minZoom,
      zooming: true
    });

    reflow();

    ready = true;

    if (focus) {
      const point = center(focus);
      scroller.scrollToViewportCenter(point.x, point.y, false, initialZoom);
    } else {
      fitToContainer();
    }
  });

  function reflow() {
    // set the correct scroller dimensions
    clientWidth = container.clientWidth;
    clientHeight = container.clientHeight;

    scroller.setDimensions(
      container.clientWidth,
      container.clientHeight,
      content.offsetWidth,
      content.offsetHeight
    );

    if (autofit) {
      fitToContainer();
    }
  }

  /**
   * Touch Events
   */

  let prevDiff = -1; // previous touch zoom diff

  function ontouchstart(event: TouchEvent) {
    scroller.doTouchStart(event.touches, event.timeStamp);
  }

  function ontouchmove(event: TouchEvent) {
    // If two pointers are down, check for pinch gestures
    if (event.touches.length >= 2) {
      let [start, end] = event.touches;
      let origin = midpoint({ x: start.pageX, y: start.pageY }, { x: end.pageX, y: end.pageY });

      // Calculate the distance between the two pointers
      let curDiff = distance(start.pageX, start.pageY, end.pageX, end.pageY);
      let zoom = (Math.abs(curDiff - prevDiff) / clientWidth) * 2.5;

      if (prevDiff > 0) {
        if (curDiff > prevDiff) {
          scroller.zoomBy(1 + zoom, false, origin.x, origin.y);
        } else {
          scroller.zoomBy(1 - zoom, false, origin.x, origin.y);
        }
      }

      // Cache the distance for the next move event
      prevDiff = curDiff;
    } else {
      dispatch("pan");
      scroller.doTouchMove(event.touches, event.timeStamp, 1);
    }
  }

  function ontouchend(event: TouchEvent) {
    scroller.doTouchEnd(event.timeStamp);

    if (event.touches.length < 2) {
      prevDiff = -1;
    }
  }

  function onmousedown(event: MouseEvent) {
    const target = event.target as HTMLElement;

    if (target.tagName.match(/input|textarea|select/i)) {
      return;
    }

    scroller.doTouchStart(
      [
        {
          pageX: event.pageX,
          pageY: event.pageY
        }
      ],
      event.timeStamp
    );

    mousedown = true;
  }

  function onmousewheel(event: WheelEvent) {
    const delta = event.deltaY > 0 ? 50 : -50;

    scroller.doMouseZoom(delta, event.timeStamp, event.pageX, event.pageY);
  }

  function onmousemove(event: MouseEvent) {
    if (!mousedown || event.buttons !== 1) {
      return;
    }

    scroller.doTouchMove(
      [
        {
          pageX: event.pageX,
          pageY: event.pageY
        }
      ],
      event.timeStamp
    );

    dispatch("pan");

    mousedown = true;
  }

  function onmouseup(event: MouseEvent) {
    if (!mousedown) {
      return;
    }

    scroller.doTouchEnd(event.timeStamp);

    mousedown = false;
  }

  function zoomToTouch(event: MouseEvent) {
    scroller.zoomTo(z + 0.75, true, event.pageX, event.pageY);
  }

  function scopeToContainer(callback: (event: Event) => void) {
    return (event: Event) => {
      const target = event.target as HTMLElement;
      if (container && container.contains(target)) {
        callback(event);
      }
    };
  }

  function fixOverflow(event: Event) {
    const target = event.target as HTMLElement;

    scroller.scrollBy(target.scrollLeft, target.scrollTop, false, $positionZ);

    target.scrollTop = 0;
    target.scrollLeft = 0;
  }
</script>

<svelte:head>
  <style>
    :root {
      --pull-refresh-behavior: contain;
    }
  </style>
</svelte:head>

<svelte:window
  on:mousedown={scopeToContainer(onmousedown)}
  on:touchstart={scopeToContainer(ontouchstart)}
  on:mousemove={onmousemove}
  on:touchmove={ontouchmove}
  on:mouseup={onmouseup}
  on:touchend={ontouchend}
  on:touchcancel={ontouchend}
  on:resize={reflow}
  on:wheel={scopeToContainer(onmousewheel)}
/>

<div
  class="Frame"
  class:ready
  bind:this={container}
  on:transitionend={reflow}
  on:scroll={fixOverflow}
>
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    class="Content"
    class:ready
    on:dblclick={zoomToTouch}
    bind:this={content}
    style="width: {width}px; height: {height}px;
           transform: translate({-x}px, {-y}px) scale({z});"
  >
    <slot />
  </div>
</div>

<noscript>
  <style>
    :root {
      --default-map-opacity: 1;
      --map-overflow: scroll;
    }
  </style>
</noscript>

<style>
  .Frame {
    background-color: var(--background-color);
    height: 100%;
    overflow: var(--map-overflow, hidden);
    position: relative;
    width: 100%;
  }

  .Frame.ready {
    overflow: hidden; /* traps scroll events */
    touch-action: none;
  }

  /* The content element needs a correct transform origin for zooming */
  .Content {
    opacity: var(--default-map-opacity, 0);
    transform-origin: 0 0;
  }

  .Content.ready {
    opacity: 1;
    transition: 0.2s opacity;
    user-select: none;
  }
</style>
